import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {Suspense, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {I18nextProvider, useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {RouterProvider} from 'react-router-dom';
import useWebSocket from 'react-use-websocket';
import './App.scss';
import {MessageId, MessageProps} from './components/alert/Message';
import SpinnerOverlay from './components/LoaderOverlay';
import {countUnreadIncidents} from './fetch/incidentApi';
import i18n from './i18n/i18n';
import {queryClient} from './queryClient';
import {getFilterDefaultsFetched} from './redux/selectors/filterDefaultsSelectors';
import {loadCategories} from './redux/slice/categorySlice';
import {fetchFilterDefaults} from './redux/slice/filterDefaultsSlice';
import {setMessage} from './redux/slice/messageSlice';
import {loadSeasons} from './redux/slice/seasonSlice';
import {unreadIncidentsSet} from './redux/slice/unreadIncidentsSlice';
import {createAppRouter} from './router';
import {RootState, useAppDispatch} from './store'; // Need manual fix for useAppDispatch;
import {PermissionModule} from './types/enums/permission/PermissionModule';
import {PermissionType} from './types/enums/permission/PermissionType';
import {SevereIncidentNotificationType} from './types/SevereIncidentNotificationType';
import {checkState} from './util/CheckState';
import {isAfter} from './util/DateUtil';
import {useCanReadAnyReport, useHasPermission, useIsAdminOrQMOrReportResponsible} from './util/PermissionUtil';

function App() {
    const loginStateRequested = useSelector((state: RootState) => state.login.requested);
    const loggedIn = useSelector((state: RootState) => state.login.loggedIn);
    const userDepartment = useSelector((state: RootState) => state.login.userData?.department);
    const showLoader = useSelector((state: RootState) => state.loader.show);
    const userRole = useSelector((state: RootState) => state.login.userData?.role);
    const userHasAcceptedDataPrivacy = useSelector((state: RootState) => !!state.login.userData?.dataPrivacyAcceptanceDate);
    const userHasAcceptedTermsOfUse = useSelector((state: RootState) => !!state.login.userData?.termsOfUseAcceptanceDate);
    const userIsRePortUser = useSelector((state: RootState) => !!state.login.userData?.role);
    const userIsActive = useSelector((state: RootState) => !!state.login.userData?.active);
    const fetched = useSelector(getFilterDefaultsFetched);

    const canSeeIncidentManagement = useHasPermission(PermissionModule.INCIDENT_MANAGEMENT, PermissionType.READ);
    const canSeeArchiveReport = useHasPermission(PermissionModule.REPORT_ARCHIVE, PermissionType.READ);
    const canSeeLiveQm = useHasPermission(PermissionModule.LIVE_QM, PermissionType.READ);
    const canSeeSummary = useHasPermission(PermissionModule.SUMMARY, PermissionType.READ);
    const canSeeUsers = useHasPermission(PermissionModule.ACCOUNT, PermissionType.READ);
    const canSeeChecklist = useHasPermission(PermissionModule.CHECKLIST, PermissionType.READ);
    const canSeeReporting = useHasPermission(PermissionModule.REPORTING, PermissionType.READ);
    const canSeeReportsForQMResponsibles = useHasPermission(PermissionModule.REPORT, PermissionType.WRITE);
    const canWriteSystemSettings = useHasPermission(PermissionModule.SYSTEM_SETTINGS, PermissionType.WRITE);
    const canSeeExtensionDeadline = useIsAdminOrQMOrReportResponsible();
    const canSeeReport = useCanReadAnyReport();

    const [showDataPrivacyModal, setShowDataPrivacyModal] = useState(false);
    const [showTermsOfUseModal, setShowTermsOfUseModal] = useState(false);
    const [showLegalHintsModal, setShowLegalHintsModal] = useState(false);

    const dispatch = useAppDispatch();
    const [t] = useTranslation();

    const websocketUrl = window.environment?.websocket
        ? window.environment.websocket + '1.0/severe-incidents'
        : null;

    const {lastMessage} = useWebSocket(websocketUrl, {
        shouldReconnect: () => false,
        share: false
    }, !!websocketUrl);

    useLayoutEffect(() => {
        const pagesWithoutLegalModals = ['terms-of-use', 'data-privacy', 'impressum', 'legal-hints'];
        if (pagesWithoutLegalModals.some(page => window.location.pathname.includes(page))) {
            return;
        }
        if (!loginStateRequested) {
            checkState(dispatch);
        }
    }, [loginStateRequested, dispatch]);

    useEffect(() => {
        if (loggedIn) {
            dispatch(loadCategories());
            dispatch(loadSeasons());
        }
    }, [loggedIn, dispatch]);

    useEffect(() => {
        if (!fetched && loggedIn) {
            dispatch(fetchFilterDefaults());
        }
    }, [dispatch, fetched, loggedIn]);

    useEffect(() => {
        if (canSeeIncidentManagement && !!userDepartment) {
            countUnreadIncidents(dispatch).then(response => {
                if (response?.success) {
                    dispatch(unreadIncidentsSet(response.result));
                    const unreadIncidents = response.result.unreadIncidents;
                    const lastNotification = window.sessionStorage.getItem('acknowledgedIncidentsNotification');

                    if (unreadIncidents > 0 &&
                        (!lastNotification || (response.result.lastIncidentAssignment && isAfter(response.result.lastIncidentAssignment, lastNotification)))) {
                        dispatch(setMessage({
                            messageId: MessageId.UNREAD_INCIDENTS,
                            message: t('message.UNREAD_INCIDENTS', {count: unreadIncidents}),
                            type: 'default',
                            autoClose: false
                        } as MessageProps));
                    }
                }
            });
        }
    }, [canSeeIncidentManagement, userDepartment, dispatch, t]);

    useEffect(() => {
        if (lastMessage !== null) {
            const severeIncidentNotification: SevereIncidentNotificationType = JSON.parse(lastMessage.data);

            if (!!severeIncidentNotification && !!severeIncidentNotification.productionName) {
                dispatch(setMessage({
                    messageId: MessageId.SEVERE_INCIDENT_NOTIFICATION,
                    message: <>
                        <strong>
                            {t('incident.NEW_SEVERE_INCIDENT')} &nbsp; {t('ProductionStage.' + severeIncidentNotification.productionStage)}
                        </strong><br/>
                        {severeIncidentNotification.productionName}</>,
                    type: 'default',
                    autoClose: false
                } as MessageProps));
            }
        }
    }, [lastMessage, t, dispatch]);

    const router = useMemo(() => createAppRouter({
        loggedIn,
        userIsActive,
        userRole,
        canSeeIncidentManagement,
        canSeeArchiveReport,
        canSeeLiveQm,
        canSeeSummary,
        canSeeUsers,
        canSeeChecklist,
        canSeeReporting,
        canSeeReportsForQMResponsibles,
        canWriteSystemSettings,
        canSeeExtensionDeadline,
        canSeeReport,
        showDataPrivacyModal,
        showTermsOfUseModal,
        showLegalHintsModal,
        setShowDataPrivacyModal,
        setShowTermsOfUseModal,
        setShowLegalHintsModal,
        userHasAcceptedDataPrivacy,
        userHasAcceptedTermsOfUse,
        userIsRePortUser
    }), [
        loggedIn,
        userIsActive,
        userRole,
        canSeeIncidentManagement,
        canSeeArchiveReport,
        canSeeLiveQm,
        canSeeSummary,
        canSeeUsers,
        canSeeChecklist,
        canSeeReporting,
        canSeeReportsForQMResponsibles,
        canWriteSystemSettings,
        canSeeExtensionDeadline,
        canSeeReport,
        showDataPrivacyModal,
        showTermsOfUseModal,
        showLegalHintsModal,
        userHasAcceptedDataPrivacy,
        userHasAcceptedTermsOfUse,
        userIsRePortUser
    ]);

    // Create a key that changes when auth state changes to force router remount
    // This ensures routes are re-evaluated when login state changes
    const routerKey = `${loggedIn}-${userIsActive}-${userIsRePortUser}`;

    return (
        <QueryClientProvider client={queryClient}>
            <Suspense fallback={null}>
                <I18nextProvider i18n={i18n}>
                    <SpinnerOverlay showLoader={showLoader}/>
                    <RouterProvider
                        router={router}
                        key={routerKey}
                    />
                </I18nextProvider>
            </Suspense>
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    );
}

export default App;
